import React from 'react';

import { useNavigate } from 'react-router-dom'; // Add this import
import { Card, Col, Row, Typography, theme, Button, Drawer, Form, Input, Steps, message, Space, Checkbox, Select, Radio, Switch, Divider, Result, Descriptions, Alert, Tooltip, Badge } from 'antd';
import { TeamOutlined, CalendarOutlined, MedicineBoxOutlined, MessageOutlined, IdcardOutlined, SearchOutlined, CheckCircleOutlined, MedicineBoxFilled } from '@ant-design/icons';

import { generateOTP, validateOTP } from '../services/AbdmM1API';

const { Title, Text } = Typography;
const { useToken } = theme;

const DashboardStats = () => {
  const { token } = useToken();
  const navigate = useNavigate(); // Add this hook
  const [drawerVisible, setDrawerVisible] = React.useState(false);
  const [drawerType, setDrawerType] = React.useState(null);
  const [currentStep, setCurrentStep] = React.useState(0);
  const [form] = Form.useForm();

  const [timeLeft] = React.useState(60);
  const [abhaNumber] = React.useState('');  
  const [abhaAddress] = React.useState('');
  const [storedTxnId, setStoredTxnId] = React.useState('');

  // Healthcare-optimized theme colors
  const themeColors = {
    // Primary - Calming blue that conveys trust and professionalism
    primary: '#2B84C4',
    // Secondary - Soothing teal that represents health and wellness
    secondary: '#41B6A6',
    // Gradient combining primary and secondary for important elements
    gradient: 'linear-gradient(135deg, #2B84C4 0%, #41B6A6 100%)',
    // Light versions for backgrounds and subtle elements
    lightPrimary: '#EBF5FC',
    lightSecondary: '#E8F6F4',
    // Accent colors for specific states
    success: '#2D8A6B',  // Muted green for positive indicators
    warning: '#E9730F',  // Soft orange for warnings
    error: '#D64242',    // Less aggressive red for errors
    textPrimary: '#2C3E50',  // Dark blue-grey for primary text
    textSecondary: '#5D7285'  // Medium blue-grey for secondary text
  };

  const showDrawer = (type) => {
    setDrawerType(type);
    setDrawerVisible(true);
    setCurrentStep(0);
    form.resetFields();
    setStoredTxnId('');
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
    setDrawerType(null);
    setCurrentStep(0);
    form.resetFields();
    setStoredTxnId('');
  };

  // Updated navigation function using React Router
  const navigateToDentalChart = () => {
    navigate('/dental-charting');
  };

  const handleABHARegistration = async (values) => {
    try {
      if (currentStep === 0) {
        console.log('Generating OTP for Aadhaar:', values.aadhaar);
        const otpResponse = await generateOTP(values.aadhaar);
        
        if (otpResponse?.responseObject?.txnId) {
          const txnId = otpResponse.responseObject.txnId;
          form.setFieldsValue({ txnId: txnId });
          setStoredTxnId(txnId);
          console.log('Stored txnId:', txnId);
          setCurrentStep(currentStep + 1);
        } else {
          console.error('No txnId received in response:', otpResponse);
          message.error('Failed to get transaction ID from OTP generation');
        }
      } else if (currentStep === 1) {
        const txnId = values.txnId || storedTxnId;
        
        console.log('Validating OTP with values:', {
          aadhaarOtp: values.aadhaarOtp,
          txnId: txnId,
          mobileNumber: values.mobileNumber,
          mobileVerification: values.mobileVerification
        });

        if (!txnId) {
          message.error('Transaction ID is missing. Please generate OTP again.');
          setCurrentStep(0);
          return;
        }

        await validateOTP(
          values.aadhaarOtp,
          txnId,
          values.mobileNumber,
          values.mobileVerification === 'verify'
        );
        setCurrentStep(currentStep + 1);
      } else {
        console.log('ABHA Registration:', values);
        setCurrentStep(currentStep + 1);
      }
    } catch (error) {
      console.error('ABHA Registration error:', error);
    }
  };

  const handleABHASearch = async (values) => {
    try {
      console.log('ABHA Search:', values);
      message.success('ABHA details found');
    } catch (error) {
      message.error('Failed to find ABHA details');
    }
  };

  const registrationSteps = [
    {
      title: 'Consent Collection',
      content: (
        <>
          <Form.Item
            name="aadhaar"
            label="Aadhaar No"
            rules={[
              { required: true, message: 'Please enter Aadhaar number' },
              { len: 12, message: 'Aadhaar number must be 12 digits' }
            ]}
          >
            <Input.Password
              placeholder="Enter Aadhaar number"
              maxLength={14}
              visibilityToggle={false}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                if (value.length <= 12) {
                  e.target.value = value.replace(/(.{4})/g, '$1 ').trim();
                }
              }}
            />
          </Form.Item>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            Please ensure that mobile number is linked with Aadhaar as it will be required for OTP authentication.
          </Text>
          <Form.Item
            name="consentAadhaar"
            valuePropName="checked"
            rules={[{ required: true, message: 'Please provide consent' }]}
          >
            <Checkbox>
            I am voluntarily sharing my Aadhaar Number / Virtual ID issued by the Unique Identification Authority of India ("UIDAI"), and my demographic information for the purpose of creating an Ayushman Bharat Health Account number ("ABHA number") and Ayushman Bharat Health Account address ("ABHA Address"). I authorize NHA to use my Aadhaar number / Virtual ID for performing Aadhaar based authentication with UIDAI as per the provisions of the Aadhaar (Targeted Delivery of Financial and other Subsidies, Benefits and Services) Act, 2016 for the aforesaid purpose. I understand that UIDAI will share my e-KYC details, or response of "Yes" with NHA upon successful authentication.
            </Checkbox>
          </Form.Item>
          <Form.Item
            name="consentLinking"
            valuePropName="checked"
            rules={[{ required: true, message: 'Please provide consent' }]}
          >
            <Checkbox>
            I consent to usage of my ABHA address and ABHA number for linking of my legacy (past) government health records and those which will be generated during this encounter.
            </Checkbox>
          </Form.Item>
          <Form.Item
            name="consentSharing"
            valuePropName="checked"
            rules={[{ required: true, message: 'Please provide consent' }]}
          >
            <Checkbox>
            I authorize the sharing of all my health records with healthcare provider(s) for the purpose of providing healthcare services to me during this encounter.
            </Checkbox>
          </Form.Item>
          <Form.Item
            name="consentAnonymization"
            valuePropName="checked"
            rules={[{ required: true, message: 'Please provide consent' }]}
          >
            <Checkbox>
            I consent to the anonymization and subsequent use of my government health records for public health purposes.
            </Checkbox>
          </Form.Item>
          <Form.Item
            name="concentAforementionedPurpose"
            valuePropName="checked"
            rules={[{ required: true, message: 'Please provide consent' }]}
          >
            <Checkbox>
            I confirm that I have duly informed and explained the beneficiary of the contents of consent for aforementioned purposes.
            </Checkbox>
          </Form.Item>
          <Form.Item
            name="consentExplained"
            valuePropName="checked"
            rules={[{ required: true, message: 'Please provide consent' }]}
          >
            <Checkbox>
            I have been explained about the consent as stated above and hereby provide my consent for the aforementioned purposes.
            </Checkbox>
          </Form.Item>
          <Form.Item
            name="authType"
            label="Authentication type"
            rules={[{ message: 'Please select authentication type' }]}
          >
            <Select defaultValue="otp">
              <Select.Option value="otp">Aadhaar OTP</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="txnId" hidden>
            <Input />
          </Form.Item>
        </>
      )
    },
    {
      title: 'Aadhaar Authentication',
      content: (
        <>
          {storedTxnId && (
            <Alert
              message={`Transaction ID: ${storedTxnId}`}
              type="info"
              style={{ marginBottom: 16 }}
              showIcon
            />
          )}
          <Form.Item
            name="aadhaarOtp"
            label="Enter OTP"
            rules={[
              { required: true, message: 'Please enter OTP' },
              { len: 6, message: 'OTP must be 6 digits' }
            ]}
          >
            <Input.OTP
              autoFocus
              length={6}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Text type="success">OTP sent successfully! Resend OTP in {timeLeft} seconds</Text>
          <Form.Item
            name="mobileNumber"
            label="Mobile Number"
            rules={[
              { required: true, message: 'Please enter mobile number' },
              { pattern: /^[6-9]\d{9}$/, message: 'Please enter valid Indian mobile number' }
            ]}
          >
            <Input placeholder="Enter mobile number" maxLength={10} />
          </Form.Item>
          <Divider />
          <Form.Item
            name="mobileVerification"
            label="Mobile Number Verification"
            initialValue="verify"
          >
            <Radio.Group>
              <Space direction="vertical">
                <Radio value="verify">Yes, verify my mobile number</Radio>
                <Radio value="skip">No, skip verification for now</Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
          <Text type="secondary">
            Using your Aadhaar-linked mobile number is recommended. Other numbers will require re-validation.
          </Text>
          <Form.Item name="txnId" hidden>
            <Input />
          </Form.Item>
        </>
      )
    },
    {
      title: 'ABHA Address Selection',
      content: (
        <>
          <Form.Item
            name="mobileOtp"
            label="Mobile OTP Verification"
          >
            <Input.OTP
              autoFocus
              length={6}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Text>OTP valid for 10 minutes</Text>
          <Form.Item
            label="Registered ABHA Number"
          >
            <Input value={abhaNumber} disabled suffix={<CheckCircleOutlined style={{ color: themeColors.primary }} />} />
          </Form.Item>
          <Form.Item
            name="enableAddressSelection"
            valuePropName="checked"
          >
            <Space direction="vertical" size="small">
              <Switch
                checkedChildren="ABHA Address Selection Enabled"
                unCheckedChildren="Enable ABHA Address Selection"
                size="default"
                style={{ width: 240 }}
              />
              <Text type="secondary">
                Enable to select your preferred ABHA address format
              </Text>
            </Space>
          </Form.Item>
          <Form.Item
            name="abhaAddress"
            label="Select ABHA Address"
          >
            <Select>
              <Select.Option value="address1">example_address1@sbx</Select.Option>
            </Select>
          </Form.Item>
        </>
      )
    },
    {
      title: 'ABHA Address Creation',
      content: (
        <Result
          status="success"
          title="ABHA Address Created Successfully!"
          subTitle="Your ABHA address has been created and linked to your account"
          extra={[
            <Descriptions column={1}>
              <Descriptions.Item label="Your ABHA Address">
                {abhaAddress}@sbx
              </Descriptions.Item>
              <Descriptions.Item label="Your ABHA Number">
                {abhaNumber}
              </Descriptions.Item>
            </Descriptions>
          ]}
        />
      )
    }
  ];

  const stats = [
    { 
      title: 'Total Patients', 
      value: 2458, 
      change: '+12%', 
      icon: <TeamOutlined />, 
      color: themeColors.primary,
      bgColor: themeColors.lightPrimary,
    },
    { 
      title: "Today's Appointments", 
      value: 37, 
      change: '-3%', 
      icon: <CalendarOutlined />, 
      color: themeColors.secondary,
      bgColor: themeColors.lightSecondary,
    },
    { 
      title: 'Pending Consultations', 
      value: 12, 
      change: '-8%', 
      icon: <MedicineBoxOutlined />, 
      color: themeColors.primary,
      bgColor: themeColors.lightPrimary,
    },
    { 
      title: 'Unread Messages', 
      value: 5, 
      change: '+2%', 
      icon: <MessageOutlined />, 
      color: themeColors.secondary,
      bgColor: themeColors.lightSecondary,
    }
  ];

  return (
    <>
      <Row justify="end" style={{ marginBottom: 24 }}>
        <Space>
          <Tooltip title="Open Dental Charting System">
            <Button 
              type="primary" 
              icon={<MedicineBoxFilled />}
              onClick={navigateToDentalChart}
              style={{ 
                background: themeColors.gradient,
                border: 'none',
                fontWeight: 600
              }}
            >
              Dental Charting
            </Button>
          </Tooltip>
          <Tooltip title="Register new ABHA number">
            <Button 
              type="primary" 
              icon={<IdcardOutlined />}
              onClick={() => showDrawer('register')}
              style={{ 
                background: themeColors.gradient,
                border: 'none'
              }}
            >
              Register ABHA
            </Button>
          </Tooltip>
          <Tooltip title="Search existing ABHA number">
            <Button 
              icon={<SearchOutlined />}
              onClick={() => showDrawer('find')}
              style={{
                borderColor: themeColors.primary,
                color: themeColors.primary
              }}
            >
              Find with ABHA
            </Button>
          </Tooltip>
        </Space>
      </Row>

      <Row gutter={[24, 32]} style={{ marginBottom: '40px' }}>
        {stats.map((stat, index) => (
          <Col key={index} xs={24} sm={12} md={6}>
            <Badge.Ribbon 
              text={stat.change} 
              color={stat.change.startsWith('+') ? themeColors.success : themeColors.error}
            >
              <Card 
                bordered={false} 
                style={{ 
                  borderRadius: '12px', 
                  boxShadow: '0 2px 8px rgba(43, 132, 196, 0.1)',
                  height: '100%',
                  background: token.colorBgContainer,
                  border: `1px solid ${themeColors.lightPrimary}`
                }}
                bodyStyle={{ padding: '24px' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div>
                    <Text style={{ 
                      fontSize: '14px',
                      fontWeight: 500,
                      color: themeColors.textSecondary
                    }}>
                      {stat.title}
                    </Text>
                    <Title level={2} style={{ 
                      margin: '8px 0',
                      color: themeColors.textPrimary,
                      fontWeight: 800
                    }}>
                      {stat.value}
                    </Title>
                    <Text type="secondary" style={{ 
                      fontSize: '12px',
                      color: themeColors.textSecondary 
                    }}>
                      vs last month
                    </Text>
                  </div>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: index % 2 === 0 ? themeColors.lightPrimary : themeColors.lightSecondary,
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {React.cloneElement(stat.icon, { 
                      style: { 
                        color: index % 2 === 0 ? themeColors.primary : themeColors.secondary,
                        fontSize: '22px',
                      } 
                    })}
                  </div>
                </div>
              </Card>
            </Badge.Ribbon>
          </Col>
        ))}
      </Row>

      <Drawer
        title={
          <Space style={{ color: themeColors.primary }}>
            {drawerType === 'register' ? <IdcardOutlined /> : <SearchOutlined />}
            {drawerType === 'register' ? 'Register ABHA' : 'Find with ABHA'}
          </Space>
        }
        placement="right"
        onClose={closeDrawer}
        open={drawerVisible}
        width="80%"
        extra={
          <Space>
            <Button onClick={closeDrawer}>Cancel</Button>
          </Space>
        }
      >
        {drawerType === 'register' ? (
          <>
            <Steps
              size="small"
              current={currentStep}
              items={[
                {
                  title: 'Consent Collection',
                  style: { textAlign: 'left' }
                },
                {
                  title: 'Aadhaar Authentication',
                  style: { textAlign: 'left' }
                },
                {
                  title: 'ABHA Address Selection',
                  style: { textAlign: 'left' }
                },
                {
                  title: 'ABHA Address Creation',
                  style: { textAlign: 'left' }
                }
              ]}
              style={{
                marginBottom: 24,
                '.ant-steps-item-icon': {
                  marginRight: 8,
                  marginLeft: 0,
                  borderColor: themeColors.primary,
                  color: themeColors.primary
                },
                '.ant-steps-icon':{
                  top: '3.5px !important',
                },
                '.ant-steps-item-active .ant-steps-item-icon': {
                  background: themeColors.gradient,
                  borderColor: 'transparent'
                }
              }}
            />
            
            <Form
              form={form}
              layout="vertical"
              onFinish={handleABHARegistration}
            >
              {registrationSteps[currentStep].content}
              <Form.Item>
                <Space>
                  {currentStep > 0 && (
                    <Button 
                      onClick={() => setCurrentStep(currentStep - 1)}
                      style={{
                        borderColor: themeColors.primary,
                        color: themeColors.primary
                      }}
                    >
                      Previous
                    </Button>
                  )}
                  <Button 
                    type="primary" 
                    htmlType="submit"
                    style={{
                      background: themeColors.gradient,
                      border: 'none'
                    }}
                  >
                    {currentStep === registrationSteps.length - 1 ? 'Create ABHA' : 'Next'}
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </>
        ) : (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleABHASearch}
          >
            <Form.Item
              name="abhaNumber"
              label="ABHA Number"
              rules={[{ required: true, message: 'Please enter ABHA number' }]}
            >
              <Input placeholder="Enter ABHA number" />
            </Form.Item>
            <Form.Item>
              <Button 
                type="primary" 
                htmlType="submit"
                style={{
                  background: themeColors.gradient,
                  border: 'none'
                }}
              >
                Search
              </Button>
            </Form.Item>
          </Form>
        )}
      </Drawer>
    </>
  );
};

export default DashboardStats;